// Parse Microsoft Secure Score JSON into the same scan + results shape we use
// for Maester. Tolerant to multiple input shapes:
//
//   { value: [ { ...secureScore }, ... ] }       ← Graph API list response
//   { ...secureScore }                           ← single score object
//   [ { ...secureScore }, ... ]                  ← top-level array of scores
//   [ { controlName, score, ... }, ... ]         ← top-level array of controls
//   { ControlScores: [...], CurrentScore: N }    ← PascalCase (PowerShell SDK)
//
// PowerShell's Get-MgSecuritySecureScore serializes properties with PascalCase,
// so we look up fields case-insensitively throughout.

export function parseSecureScoreJson(input) {
  const parseWarnings = []
  let payload = input

  if (typeof input === 'string') {
    try { payload = JSON.parse(input) }
    catch (err) {
      throw new Error(`Invalid JSON: ${err.message}`)
    }
  }
  if (!payload || typeof payload !== 'object') {
    throw new Error('Empty or non-object payload')
  }

  const score = pickScoreObject(payload, parseWarnings)
  if (!score) {
    const topKeys = Array.isArray(payload)
      ? `array of ${payload.length} items` + (payload[0] && typeof payload[0] === 'object' ? ` with keys: ${Object.keys(payload[0]).slice(0, 8).join(', ')}` : '')
      : `keys: ${Object.keys(payload).slice(0, 12).join(', ')}`
    throw new Error(`Could not locate a Secure Score record. Saw ${topKeys}. Expected an object with controlScores/ControlScores, or a Graph API response with value:[…].`)
  }

  const controlScoresRaw = getProp(score, ['controlScores', 'ControlScores'])
  const controlScores = Array.isArray(controlScoresRaw) ? controlScoresRaw : []
  if (controlScores.length === 0) {
    parseWarnings.push('Secure Score record contains no controlScores. The summary will show but no per-control rows will be created.')
  }

  const results = controlScores.map(c => mapControl(c)).filter(Boolean)

  const totals = {
    total_tests: results.length,
    passed_count: results.filter(r => r.result === 'passed').length,
    failed_count: results.filter(r => r.result === 'failed').length,
    skipped_count: results.filter(r => r.result === 'skipped').length,
    not_run_count: results.filter(r => r.result === 'not_run').length
  }

  // Per-category rollup for the summary card
  const byCategory = {}
  for (const r of results) {
    const cat = r.category || 'Uncategorized'
    if (!byCategory[cat]) byCategory[cat] = { score: 0, max: 0, controls: 0, passed: 0, failed: 0, partial: 0 }
    const meta = r.metadata || {}
    byCategory[cat].score += Number(meta.score || 0)
    byCategory[cat].max += Number(meta.maxScore || 0)
    byCategory[cat].controls++
    if (r.result === 'passed') byCategory[cat].passed++
    else if (r.result === 'failed') byCategory[cat].failed++
    else if (r.result === 'inconclusive') byCategory[cat].partial++
  }

  // Pull aggregate score: prefer explicit fields, otherwise sum from controls
  let currentScore = Number(getProp(score, ['currentScore', 'CurrentScore'])) || 0
  let maxScore = Number(getProp(score, ['maxScore', 'MaxScore'])) || 0
  if (!currentScore && results.length > 0) {
    currentScore = results.reduce((s, r) => s + (Number(r.metadata?.score) || 0), 0)
  }
  if (!maxScore && results.length > 0) {
    maxScore = results.reduce((s, r) => s + (Number(r.metadata?.maxScore) || 0), 0)
  }
  const percentage = maxScore > 0 ? Math.round((currentScore / maxScore) * 100) : null

  const enabledServices = getProp(score, ['enabledServices', 'EnabledServices'])
  const compare = getProp(score, ['averageComparativeScores', 'AverageComparativeScores'])

  return {
    scan: {
      tenant_id: getProp(score, ['azureTenantId', 'AzureTenantId', 'tenantId', 'TenantId']) || null,
      tenant_domain: null,
      tool_version: null,
      scanned_at: normalizeDate(getProp(score, ['createdDateTime', 'CreatedDateTime'])) || new Date().toISOString(),
      ...totals,
      raw_payload: {
        scan_type: 'secure_score',
        currentScore: Math.round(currentScore * 10) / 10,
        maxScore: Math.round(maxScore * 10) / 10,
        percentage,
        activeUserCount: getProp(score, ['activeUserCount', 'ActiveUserCount']) ?? null,
        licensedUserCount: getProp(score, ['licensedUserCount', 'LicensedUserCount']) ?? null,
        enabledServices: Array.isArray(enabledServices) ? enabledServices : [],
        averageComparativeScores: Array.isArray(compare) ? compare : [],
        byCategory
      }
    },
    results,
    parseWarnings
  }
}

// ─── Internals ───

function pickScoreObject(payload, parseWarnings) {
  // Top-level array — could be historical scores, or a flat list of controls.
  if (Array.isArray(payload)) {
    if (payload.length === 0) return null
    const looksLikeScore = payload[0] && (
      Array.isArray(getProp(payload[0], ['controlScores', 'ControlScores'])) ||
      getProp(payload[0], ['currentScore', 'CurrentScore']) !== undefined
    )
    if (looksLikeScore) {
      return pickLatest(payload, parseWarnings)
    }
    const looksLikeControl = payload[0] && (
      payload[0].controlName !== undefined || payload[0].ControlName !== undefined ||
      (payload[0].id !== undefined && (payload[0].score !== undefined || payload[0].Score !== undefined))
    )
    if (looksLikeControl) {
      parseWarnings.push('No parent score record found; treating top-level array as controlScores and synthesizing a wrapper.')
      return { controlScores: payload }
    }
    return null
  }

  // Direct object with controlScores or aggregate fields
  if (Array.isArray(getProp(payload, ['controlScores', 'ControlScores'])) ||
      getProp(payload, ['currentScore', 'CurrentScore']) !== undefined) {
    return payload
  }

  // Graph API list response
  const valueArray = getProp(payload, ['value', 'Value'])
  if (Array.isArray(valueArray)) {
    const scores = valueArray.filter(s => s && (
      Array.isArray(getProp(s, ['controlScores', 'ControlScores'])) ||
      getProp(s, ['currentScore', 'CurrentScore']) !== undefined
    ))
    if (scores.length > 0) return pickLatest(scores, parseWarnings)
  }

  // Common wrapper keys
  for (const key of ['secureScore', 'SecureScore', 'secureScores', 'SecureScores', 'data', 'Data', 'result', 'Result']) {
    if (payload[key]) {
      const inner = pickScoreObject(payload[key], parseWarnings)
      if (inner) return inner
    }
  }
  return null
}

function pickLatest(scores, parseWarnings) {
  scores.sort((a, b) => {
    const aDate = new Date(getProp(a, ['createdDateTime', 'CreatedDateTime']) || 0)
    const bDate = new Date(getProp(b, ['createdDateTime', 'CreatedDateTime']) || 0)
    return bDate - aDate
  })
  if (scores.length > 1) {
    const dt = getProp(scores[0], ['createdDateTime', 'CreatedDateTime']) || 'unknown date'
    parseWarnings.push(`File contains ${scores.length} historical scores; using the most recent (${dt}).`)
  }
  return scores[0]
}

function mapControl(c) {
  if (!c || typeof c !== 'object') return null
  const name = getProp(c, ['controlName', 'ControlName', 'id', 'Id'])
  if (!name) return null

  const score = Number(getProp(c, ['score', 'Score'])) || 0
  const declaredMax = Number(getProp(c, ['total', 'Total', 'maxScore', 'MaxScore'])) || 0
  const pctRaw = getProp(c, ['scoreInPercentage', 'ScoreInPercentage'])
  const pct = typeof pctRaw === 'number' ? pctRaw : null
  const maxScore = declaredMax > 0
    ? declaredMax
    : (pct === 100 ? score : (pct && pct > 0 ? score / (pct / 100) : 0))

  const isApplicable = getProp(c, ['IsApplicable', 'isApplicable'])
  const onFlag = getProp(c, ['on', 'On'])

  let result
  if (isApplicable === false) {
    result = 'skipped'
  } else if (pct === null && score === 0 && maxScore === 0) {
    result = 'not_run'
  } else if (pct === 100 || (maxScore > 0 && score >= maxScore)) {
    result = 'passed'
  } else if (pct === 0 || score === 0) {
    result = 'failed'
  } else {
    result = 'inconclusive'
  }

  const tags = []
  if (onFlag === false) tags.push('off')
  const actionType = getProp(c, ['actionType', 'ActionType'])
  if (actionType) tags.push(`action:${actionType}`)
  const tier = getProp(c, ['tier', 'Tier'])
  if (tier) tags.push(`tier:${tier}`)

  return {
    test_id: String(name).slice(0, 200),
    test_name: truncate(getProp(c, ['title', 'Title']) || name, 500),
    description: truncate(getProp(c, ['description', 'Description']), 4000),
    category: truncate(getProp(c, ['controlCategory', 'ControlCategory', 'controlGroup', 'ControlGroup']) || null, 200),
    severity: deriveSeverity(c),
    result,
    result_message: truncate(getProp(c, ['implementationStatus', 'ImplementationStatus']), 2000),
    remediation: truncate(getProp(c, ['remediation', 'Remediation']), 4000),
    reference_url: truncate(getProp(c, ['actionUrl', 'ActionUrl', 'helpUrl', 'HelpUrl']) || null, 1000),
    framework_tags: ['Microsoft Secure Score'],
    tags: tags.slice(0, 50),
    metadata: {
      score,
      maxScore: Math.round(maxScore * 10) / 10,
      percentage: pct !== null ? pct : (maxScore > 0 ? Math.round((score / maxScore) * 1000) / 10 : null),
      lastSynced: getProp(c, ['lastSynced', 'LastSynced']) || null,
      on: onFlag,
      rank: getProp(c, ['rank', 'Rank']) ?? null
    }
  }
}

function deriveSeverity(c) {
  const rank = Number(getProp(c, ['rank', 'Rank']))
  if (Number.isFinite(rank) && rank > 0) {
    if (rank <= 10) return 'critical'
    if (rank <= 30) return 'high'
    if (rank <= 70) return 'medium'
    return 'low'
  }
  const userImpact = getProp(c, ['userImpact', 'UserImpact'])
  if (typeof userImpact === 'string') {
    const v = userImpact.toLowerCase()
    if (v === 'high') return 'high'
    if (v === 'moderate') return 'medium'
    if (v === 'low') return 'low'
  }
  return null
}

function getProp(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k]
  }
  return undefined
}

function truncate(value, max) {
  if (value === null || value === undefined) return null
  const s = typeof value === 'string' ? value : String(value)
  return s.length > max ? s.slice(0, max) + '…' : s
}

function normalizeDate(value) {
  if (!value) return null
  try {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d.toISOString()
  } catch { return null }
}
